<template>
    <div class="space-y-6">
        <envelope-title
            v-model="title"
            :editing="creating || editing"
        />

        <envelope-status
            v-if="envelope && (! creating && ! editing)"
            :envelope="envelope"
            :envelopes_available="envelopes_available"
            :signature_purchase_route="signature_purchase_route"
            @viewDocusign="viewDocusign"
            @recipientStatusUpdated="updateRecipientStatus"
        />

        <envelope-documents
            v-model="documents"
            :folders="folders"
            :all_documents="all_documents"
            :selected_document_ids="selected_document_ids"
            :editing="creating || editing"
        />

        <envelope-recipients
            v-model="recipients"
            :editing="creating || editing"
            :statuses="recipient_statuses"
        />

        <div v-if="creating"
            class="flex justify-between"
        >
            <div class="flex-none">
                <button-blue-outline @click="$emit('cancel')">
                    Cancel
                </button-blue-outline>
            </div>

            <div class="flex flex-1 justify-end items-center space-x-6 pl-12">
                <div v-if="has_documents && recipients_are_valid" class="text-blue-600 text-right leading-snug">
                    Click <span class="text-sm font-semibold uppercase tracking-wide mr-0.5">continue</span> to add signature fields to your document and send it with DocuSign.
                </div>

                <button-blue @click="saveEnvelope"
                    :disabled="! has_documents || ! recipients_are_valid"
                >
                    <span class="px-4">Continue</span>
                </button-blue>
            </div>
        </div>

        <div v-else-if="editing"
            class="flex justify-between"
        >
            <div class="flex-none">
                <button-blue-outline @click="cancelChanges">
                    Cancel
                </button-blue-outline>
            </div>

            <div class="flex flex-1 justify-end items-center space-x-6 pl-12">
                <button-blue @click="saveEnvelope">
                    <span class="px-4">Save Changes</span>
                </button-blue>
            </div>
        </div>

        <div v-else
            class="flex justify-between"
        >
            <div class="flex-none">
                <a :href="signatures_index_route">
                    <button-blue-outline>
                        <i class="fas fa-arrow-left mr-1 -ml-1" />
                        Back
                    </button-blue-outline>
                </a>
            </div>

            <div v-if="! envelope.completed_at"
                class="flex flex-1 justify-end items-center space-x-6 pl-12"
            >
                <button-text-red v-if="! envelope.sent_at"
                    @click="deleteEnvelope"
                >
                    <i class="far fa-trash-alt mr-1"></i>
                    Delete Envelope
                </button-text-red>

                <button-blue  v-if="! envelope.sent_at"
                    @click="editEnvelope"
                >
                    <span class="px-4">Edit Envelope</span>
                </button-blue>
            </div>
        </div>

        <docusign-loading-modal
            :show="loading"
        />

        <envelope-saving-modal
            :show="saving"
        />
    </div>
</template>

<script>
import ButtonBlue from '../../../components/button-blue.vue';
import ButtonBlueOutline from '../../../components/button-blue-outline.vue';
import ButtonTextRed from '../../../components/button-text-red.vue';
import DocusignLoadingModal from './docusign-loading-modal.vue';
import EnvelopeDocuments from './envelope-documents.vue';
import EnvelopeRecipients from './envelope-recipients.vue';
import EnvelopeSavingModal from './envelope-saving-modal.vue';
import EnvelopeStatus from './envelope-status.vue';
import EnvelopeTitle from './envelope-title.vue';

export default {
    components: {
        ButtonBlue,
        ButtonBlueOutline,
        ButtonTextRed,
        DocusignLoadingModal,
        EnvelopeRecipients,
        EnvelopeSavingModal,
        EnvelopeStatus,
        EnvelopeTitle,
        EnvelopeDocuments,
    },

    props: {
        creating: { default: false },
        property: {},
        folders: {},
        all_documents: {},
        envelope_count: {},
        envelope: {},
        envelopes_available: {},
        signature_purchase_route: {},
        signatures_create_route: {},
        signatures_index_route: {},
        signatures_view_docusign_route: {},
    },

    data() {
        return {
            envelope_id: null,
            title: this.getDefaultTitle(),
            recipients: [],
            documents: [],
            selected_document_ids: [],
            loading: false,
            editing: false,
            saving: false,
            recipient_statuses: [],
        }
    },

    computed: {
        has_documents() {
            return this.documents.length > 0;
        },
        has_recipients() {
            return !! this.recipients.filter(recipient => recipient.name || recipient.email).length;
        },
        recipients_are_valid() {
            return this.has_recipients
                && this.recipients.every(recipient => recipient.name && this.emailIsValid(recipient.email));
        },
    },

    methods: {
        getDefaultTitle() {
            return 'Envelope #' + (parseInt(this.envelope_count) + 1);
        },
        emailIsValid(email) {
            return String(email)
                .toLowerCase()
                .match(
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                );
        },
        saveEnvelope() {
            if (! this.envelope_id) {
                this.createEnvelope();
                return;
            }

            this.saving = true;

            axios.put(this.signatures_create_route + '/' + this.envelope_id, {
                title: this.title || this.getDefaultTitle(),
                documents: this.documents,
                recipients: this.recipients,
            }).then(response => {
                //
            }).catch(error => {
                if (error.response.data.error) {
                    alert(error.response.data.error);
                }
            }).finally(() => {
                this.saving = false;
                this.editing = false;
            });
        },
        createEnvelope() {
            this.loading = true;

            axios.post(this.signatures_create_route, {
                title: this.title || this.getDefaultTitle(),
                documents: this.documents,
                recipients: this.recipients,
            }).then(response => {
                this.envelope_id = response.data?.id;

                if (response.data.docusign_url) {
                    window.location.href = response.data.docusign_url;
                }
            }).catch(error => {
                if (error.response.data.error) {
                    alert(error.response.data.error);
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        deleteEnvelope() {
            axios.delete(this.signatures_create_route + '/' + this.envelope_id)
                .then(response => {
                    window.location.href = this.signatures_index_route;
                }).catch(error => {
                    if (error.response.data.error) {
                        alert(error.response.data.error);
                    }
                });
        },
        viewDocusign() {
            this.loading = true;

            axios.post(this.signatures_view_docusign_route)
                .then(response => {
                    if (response.data.docusign_url) {
                        window.location.href = response.data.docusign_url;
                    }
                }).catch(error => {
                    if (error.response.data.error) {
                        alert(error.response.data.error);
                    }
                }).finally(() => {
                    this.loading = false;
                });
        },
        editEnvelope() {
            this.editing = true;
        },
        cancelChanges() {
            window.location.reload();
        },
        getCurrentEnvelopeValues() {
            this.envelope_id = this.envelope.id;
            this.title = this.envelope.title;
            this.documents = this.envelope.property_documents?.map(document => document.id);
            this.selected_document_ids = this.documents;
            this.recipients = this.envelope.recipients;
        },
        updateRecipientStatus(statuses) {
            this.recipient_statuses = statuses;
        },
    },

    created() {
        if (this.envelope) {
            this.getCurrentEnvelopeValues();
        }
    },
}
</script>
